import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Activity, DollarSign } from 'lucide-react'
import { motion } from 'framer-motion'
import { fadeInUp, staggerContainer } from '@/lib/motion'
import { cn } from '@/lib/utils'

export function StatisticsScreen() {
    const navigate = useNavigate()

    return (
        <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            className="flex flex-col h-full bg-background"
        >
            {/* Header */}
            <motion.header variants={fadeInUp} className="flex-shrink-0 flex items-center gap-4 px-6 py-4 safe-area-top sticky top-0 bg-background/80 backdrop-blur-xl border-b border-foreground/5 z-40">
                <button
                    onClick={() => navigate(-1)}
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-foreground/5 hover:bg-foreground/10 active:scale-95 transition-all text-foreground/80"
                >
                    <ArrowLeft className="w-5 h-5" />
                </button>
                <h1 className="text-xl font-bold text-foreground tracking-wide">統計數據</h1>
            </motion.header>

            <motion.main
                variants={staggerContainer}
                className="flex-1 overflow-y-auto px-6 py-6 scrollbar-hide space-y-6 pb-24"
            >
                {/* Main Graph Card (Simulated) */}
                <motion.section variants={fadeInUp} className="p-6 rounded-3xl bg-surface-elevated border border-foreground/5 relative overflow-hidden">
                    <div className="flex justify-between items-start mb-6 relative z-10">
                        <div>
                            <p className="text-xs text-muted-foreground font-bold uppercase tracking-wider mb-1">淨利潤走勢</p>
                            <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
                                $12,540
                                <span className="text-sm font-bold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full">+12.5%</span>
                            </h2>
                        </div>
                        <div className="flex gap-1">
                            {['1D', '1W', '1M', 'All'].map((t, i) => (
                                <button key={t} className={cn(
                                    "px-2 py-1 rounded text-xs font-bold transition-colors",
                                    i === 2 ? "bg-foreground/10 text-foreground" : "text-foreground/20 hover:text-muted-foreground"
                                )}>{t}</button>
                            ))}
                        </div>
                    </div>

                    {/* Simulated Graph Lines */}
                    <div className="h-32 flex items-end justify-between gap-1 opacity-80">
                        {[40, 65, 50, 80, 55, 90, 70, 85, 60, 95, 75, 100].map((h, i) => (
                            <motion.div
                                key={i}
                                initial={{ height: 0 }}
                                animate={{ height: `${h}%` }}
                                transition={{ delay: i * 0.05, duration: 0.5 }}
                                className={cn(
                                    "flex-1 rounded-t-sm",
                                    i % 2 === 0 ? "bg-amber-500/20" : "bg-amber-500/40"
                                )}
                            />
                        ))}
                    </div>
                </motion.section>

                {/* Key Stats Grid */}
                <motion.section variants={fadeInUp} className="grid grid-cols-2 gap-3">
                    <div className="p-4 rounded-2xl bg-surface-elevated border border-foreground/5 space-y-2">
                        <div className="w-8 h-8 rounded-full bg-emerald-500/10 flex items-center justify-center text-emerald-500">
                            <Target className="w-4 h-4" />
                        </div>
                        <div>
                            <span className="text-2xl font-bold text-foreground">28%</span>
                            <p className="text-xs text-muted-foreground">入池率 (VPIP)</p>
                        </div>
                    </div>
                    <div className="p-4 rounded-2xl bg-surface-elevated border border-foreground/5 space-y-2">
                        <div className="w-8 h-8 rounded-full bg-rose-500/10 flex items-center justify-center text-rose-500">
                            <Swords className="w-4 h-4" />
                        </div>
                        <div>
                            <span className="text-2xl font-bold text-foreground">18%</span>
                            <p className="text-xs text-muted-foreground">加注率 (PFR)</p>
                        </div>
                    </div>
                    <div className="p-4 rounded-2xl bg-surface-elevated border border-foreground/5 space-y-2">
                        <div className="w-8 h-8 rounded-full bg-amber-500/10 flex items-center justify-center text-amber-500">
                            <Activity className="w-4 h-4" />
                        </div>
                        <div>
                            <span className="text-2xl font-bold text-foreground">3.5</span>
                            <p className="text-xs text-muted-foreground">激進指數 (AF)</p>
                        </div>
                    </div>
                    <div className="p-4 rounded-2xl bg-surface-elevated border border-foreground/5 space-y-2">
                        <div className="w-8 h-8 rounded-full bg-purple-500/10 flex items-center justify-center text-purple-500">
                            <DollarSign className="w-4 h-4" />
                        </div>
                        <div>
                            <span className="text-2xl font-bold text-foreground">12BB</span>
                            <p className="text-xs text-muted-foreground">百手盈利</p>
                        </div>
                    </div>
                </motion.section>

                {/* Position Stats */}
                <motion.section variants={fadeInUp} className="space-y-3">
                    <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wider pl-1">位置數據</h3>
                    <div className="space-y-2">
                        {[
                            { pos: 'Button', win: 65, pnl: 450 },
                            { pos: 'Cutoff', win: 58, pnl: 230 },
                            { pos: 'MP', win: 45, pnl: -50 },
                            { pos: 'UTG', win: 42, pnl: -120 },
                            { pos: 'Big Blind', win: 35, pnl: -280 },
                        ].map((item, i) => (
                            <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-foreground/5 border border-foreground/5">
                                <div className="flex items-center gap-3">
                                    <span className="text-sm font-bold text-foreground w-12">{item.pos}</span>
                                    <div className="h-1.5 w-24 bg-foreground/10 rounded-full overflow-hidden">
                                        <div className="h-full bg-amber-500 rounded-full" style={{ width: `${item.win}%` }} />
                                    </div>
                                </div>
                                <div className="text-right">
                                    <span className={cn("text-xs font-bold", item.pnl > 0 ? "text-emerald-400" : "text-rose-400")}>
                                        {item.pnl > 0 ? '+' : ''}{item.pnl} BB
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </motion.section>
            </motion.main>
        </motion.div>
    )
}

function Target(props: any) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24" height="24" viewBox="0 0 24 24"
            fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            {...props}
        >
            <circle cx="12" cy="12" r="10" />
            <circle cx="12" cy="12" r="6" />
            <circle cx="12" cy="12" r="2" />
        </svg>
    )
}

function Swords(props: any) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24" height="24" viewBox="0 0 24 24"
            fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            {...props}
        >
            <polyline points="14.5 17.5 3 6 3 3 6 3 17.5 14.5" />
            <line x1="13" y1="19" x2="19" y2="13" />
            <line x1="16" y1="16" x2="20" y2="20" />
            <line x1="19" y1="21" x2="21" y2="19" />
            <polyline points="14.5 6.5 18 3 21 3 21 6 17.5 9.5" />
            <line x1="16" y1="14" x2="20" y2="10" />
            <line x1="17" y1="17" x2="19" y2="15" />
            <line x1="21" y1="21" x2="15" y2="15" />
        </svg>
    )
}
